/**
 * Where the widget's own files live — the loader, the main bundle, the
 * stylesheet, the mp3 wasm and every `/api/*` route the widget calls.
 *
 * It used to be one hardcoded string in five files, which meant an embed
 * pointing at `widgets.flowapt.com` still fetched its config, its 1.3 MB
 * bundle and every message POST from `react-chatwidget-flowpat.vercel.app`:
 * the branded host was cosmetic. Now we take the origin the script was
 * actually served from, so everything follows the embed.
 *
 * Only OUR hosts are trusted for that. A merchant who copied `loader.js` onto
 * their own CDN gets the canonical host back — their CDN serves the file but
 * not `/api`, so deriving blindly would break them.
 *
 * Captured at module-evaluation time on purpose: `document.currentScript` is
 * the running <script> only while it executes synchronously, and is null by
 * the time anything async (a click, a fetch) calls in.
 */
export const FLOWIQ_ASSET_FALLBACK = "https://react-chatwidget-flowpat.vercel.app";

export const resolveAssetBase = (scriptSrc: string | undefined | null): string => {
  try {
    if (!scriptSrc) return FLOWIQ_ASSET_FALLBACK;
    const url = new URL(scriptSrc, location.href);
    const host = url.hostname.toLowerCase();
    const ours =
      host === "flowapt.com" ||
      host.endsWith(".flowapt.com") ||
      host.endsWith(".vercel.app");
    // NOT localhost: a plain `vite dev` serves the bundle but no /api routes,
    // and dev has always talked to the deployed API. Keep that.
    return ours ? url.origin : FLOWIQ_ASSET_FALLBACK;
  } catch {
    return FLOWIQ_ASSET_FALLBACK;
  }
};

const SELF_SRC =
  typeof document !== "undefined"
    ? (document.currentScript as HTMLScriptElement | null)?.src || null
    : null;

/** The origin this bundle was served from, or the canonical host. */
export const ASSET_BASE = resolveAssetBase(SELF_SRC);
